<template>
    <div>
        <ErrorMessage v-if="error" :value="error" class="mb-3" />

        <ErrorMessage v-if="!pluginHasCampaignPriority" :value="$t('pluginExtension.rotatingGroups.noPrioritiesWarning')" class="mb-3" />

        <template v-else>
            <p class="small text-muted mb-2">{{ $t('pluginExtension.rotatingGroups.extensionHint') }}</p>

            <div class="d-flex flex-wrap align-items-center mb-3">
                <ButtonIcon size="sm" class="me-2 mb-2" @click="addGroupDefinition">
                    {{ $t('pluginExtension.rotatingGroups.addGroup') }}
                </ButtonIcon>

                <b-form-checkbox v-model="excludeNonStrategicCampaigns" class="mb-2 ml-4" :value="true" :unchecked-value="false">
                    {{ $t('pluginExtension.rotatingGroups.excludeNonStrategicCampaigns') }}
                </b-form-checkbox>
            </div>

            <div v-for="(definition, index) in definitions" :key="`group-definition-${index}`" class="border rounded p-3 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <strong>{{ $t('pluginExtension.rotatingGroups.nthGroup') }} {{ index + 1 }}</strong>
                    <ButtonIcon size="sm" variant="danger" @click="removeGroupDefinition(index)">
                        {{ $t('pluginExtension.rotatingGroups.removeGroup') }}
                    </ButtonIcon>
                </div>

                <div class="row">
                    <div class="col-md-6 mb-2">
                        <b-form-group :label="$t('pluginExtension.rotatingGroups.positionFrom')" label-for="positionFrom">
                            <b-form-input
                                id="positionFrom"
                                v-model="definition.from"
                                type="number"
                                min="1"
                                step="1"
                                @change="rangeInputValueChange(index, 'from')"
                            />
                        </b-form-group>
                    </div>
                    <div class="col-md-6 mb-2">
                        <b-form-group :label="$t('pluginExtension.rotatingGroups.positionTo')" label-for="positionTo">
                            <b-form-input
                                id="positionTo"
                                v-model="definition.to"
                                type="number"
                                min="1"
                                step="1"
                                :readonly="definition.from === null || definition.from === ''"
                                @change="rangeInputValueChange(index, 'to')"
                            />
                        </b-form-group>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import { cloneDeep } from 'lodash';
    import pluginService from '@/services/pluginService';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';

    export default {
        name: 'RotatingGroupsExtension',
        components: {
            ErrorMessage,
        },
        props: {
            settings: {
                type: Object,
                default: () => ({}),
            },

            plugin: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                error: null,
                excludeNonStrategicCampaigns: false,
                definitions: [],
                activeCampaigns: [],
            };
        },
        computed: {
            pluginHasCampaignPriority() {
                if (!this.activeCampaigns || !this.activeCampaigns.length) {
                    return true;
                }

                return this.activeCampaigns.some(campaign => campaign.priority > 0);
            },
        },
        methods: {
            async loadActiveCampaigns() {
                try {
                    const response = await pluginService.fetchActiveCampaigns(this.plugin.id);
                    this.activeCampaigns = Object.values(response || {});
                } catch (err) {
                    this.error = err;
                    this.activeCampaigns = [];
                }
            },
            loadSettings() {
                if (this.settings) {
                    this.excludeNonStrategicCampaigns = this.settings.excludeNonStrategicCampaigns || false;
                    this.definitions = this.settings.definitions ? cloneDeep(this.settings.definitions) : [];
                }
            },
            addGroupDefinition() {
                this.definitions.push({ from: null, to: null });
            },
            removeGroupDefinition(index) {
                this.definitions.splice(index, 1);
            },
            rangeInputValueChange(index, parameter) {
                let value = parseInt(this.definitions[index][parameter], 10);
                value = Number.isNaN(value) || value < 1 ? 1 : value;
                if (parameter === 'to' && value < this.definitions[index].from) {
                    value = this.definitions[index].from;
                }
                this.$set(this.definitions, index, { ...this.definitions[index], [parameter]: value });
            },
            getExtensionSettings() {
                return {
                    excludeNonStrategicCampaigns: this.excludeNonStrategicCampaigns,
                    definitions: this.definitions,
                };
            },
        },
        async mounted() {
            await this.loadActiveCampaigns();
            this.loadSettings();
        },
    };
</script>

<style scoped>
    .groups-list-item {
        padding: 10px;
        margin-bottom: 15px;
        border: 1px solid #e8e8e8;
        min-height: 70px;
    }
</style>
